import {
  Chip,
  ChipsList,
  ChipsSeparator,
  FilterChip,
  IconBadge,
  StatusType,
} from '@ardoq/status-ui';
import { SearchInput } from '@ardoq/forms';
import { FilterListIcon, Icon, IconName, IconSize } from '@ardoq/icons';
import { AiButton, BUTTON_SELECTOR, TertiaryButton } from '@ardoq/button';
import styled from 'styled-components';
import { colors, s16, s8 } from '@ardoq/design-tokens';
import { CSSProperties } from 'react';
import { FlexBox } from '@ardoq/layout';
import { FilterValue } from '@ardoq/api-types';
import { type I18nLocale, t } from '@ardoq/i18n';

export type FilterBarValue = FilterValue | FilterValue[];

export type ActiveBarFilters = Array<{
  value: FilterBarValue;
  label: string;
  type: string;
  iconName: IconName;
  rightIconName?: IconName;
  isDisabled?: boolean;
  isInvalid?: boolean;
}>;

interface SearchAndFilterBarProps {
  activeFilters: ActiveBarFilters;
  removeFilter: (filterValue: FilterBarValue, type: string) => void;
  clearAllFilters: VoidFunction;
  onSearch?: (searchValue: string) => void;
  searchValue?: string;
  onFilterButtonClick: VoidFunction;
  searchDisabled?: boolean;
  barContainerProps?: BarContainerProps;
  clearAllButtonDisabled?: boolean;
  aiButtonProps?: {
    isActive: boolean;
    onClick: VoidFunction;
  };
  i18nLocale?: I18nLocale;
}

type BarContainerProps = {
  $padding?: CSSProperties['padding'];
  $containerHeight?: CSSProperties['height'];
  $disableBottomBorder?: boolean;
};

const SEARCH_AND_FILTER_BAR_HEIGHT = '68px';
const SearchAndFilterBarContainer = styled(FlexBox).attrs({
  gap: 'medium',
})<BarContainerProps>`
  height: ${({ $containerHeight }) =>
    $containerHeight ? $containerHeight : SEARCH_AND_FILTER_BAR_HEIGHT};
  padding: ${({ $padding }) => ($padding ? $padding : `${s16}`)};
  width: 100%;

  ${BUTTON_SELECTOR} {
    flex: 0 0 auto;
  }

  border-bottom: ${({ $disableBottomBorder }) =>
    $disableBottomBorder ? 'none' : `1px solid ${colors.borderAction}`};
`;

const ChipsListAndClearAllButtonContainer = styled(FlexBox)`
  margin-right: auto; /* This is to push the filter button to the right */
  min-width: 400px;
`;

const getChipKey = (
  value: FilterBarValue,
  index: number,
  type: string
): string => {
  const stringifiedValue = value?.toString();
  const valueBasedKey = stringifiedValue ? stringifiedValue : index.toString(); // some values produce '' after toString() - use index in this case to avoid duplicating keys
  return `${valueBasedKey}-${type}`; // type is added to cover potential identical values
};

const SearchAndFilterBar = ({
  activeFilters,
  removeFilter,
  clearAllFilters,
  onSearch,
  searchValue,
  onFilterButtonClick,
  searchDisabled,
  barContainerProps,
  clearAllButtonDisabled,
  aiButtonProps,
  i18nLocale = {},
}: SearchAndFilterBarProps) => {
  const validFilters = activeFilters.filter(({ isInvalid }) => !isInvalid);
  const filterLabel = t(
    i18nLocale,
    'snowflakes:searchAndFilterBar.filterLabel',
    'Filter'
  );
  return (
    <SearchAndFilterBarContainer {...barContainerProps}>
      {!searchDisabled && onSearch && (
        <SearchInput
          onValueChange={onSearch}
          placeholder={t(
            i18nLocale,
            'snowflakes:searchAndFilterBar.searchPlaceholder',
            'Search by data name'
          )}
          value={searchValue ?? ''}
        />
      )}
      <ChipsListAndClearAllButtonContainer gap="small" align="center">
        <ChipsList
          aria-label={t(
            i18nLocale,
            'snowflakes:searchAndFilterBar.activeFiltersLabel',
            'Active filters'
          )}
          role="list"
        >
          {validFilters.map(
            (
              { label, value, iconName, type, rightIconName, isDisabled },
              index
            ) => (
              <FilterChip
                role="listitem"
                label={label}
                key={getChipKey(value, index, type)}
                iconName={iconName}
                onClose={() => removeFilter(value, type)}
                isDisabled={isDisabled}
                i18nLocale={i18nLocale}
              >
                <FlexBox align={'center'} gap={'xsmall'}>
                  {label}
                  {rightIconName ? (
                    <Icon iconName={rightIconName} iconSize={IconSize.SMALL} />
                  ) : null}
                </FlexBox>
              </FilterChip>
            )
          )}
        </ChipsList>
        {Boolean(validFilters.length) && !clearAllButtonDisabled && (
          <>
            <ChipsSeparator />
            <Chip onClick={clearAllFilters} data-test-id="clear-all-chip">
              {t(
                i18nLocale,
                'snowflakes:searchAndFilterBar.clearAll',
                'Clear all'
              )}
            </Chip>
          </>
        )}
      </ChipsListAndClearAllButtonContainer>
      <TertiaryButton
        onClick={onFilterButtonClick}
        dataTestId="search-and-filter-bar-filter-button"
        aria-label={t(
          i18nLocale,
          'snowflakes:searchAndFilterBar.openFilterSidebarLabel',
          'Open filter sidebar'
        )}
      >
        {activeFilters.length > 0 ? (
          <IconBadge
            iconName={IconName.FILTER_LIST}
            statusType={StatusType.NEW}
            style={{ margin: `0 ${s8}` }}
            iconStyle={{ margin: 0 }}
          />
        ) : (
          <FilterListIcon />
        )}
        {`${filterLabel} ${activeFilters.length ? ` (${activeFilters.length})` : ''}`}
      </TertiaryButton>
      {aiButtonProps && (
        <AiButton
          isActive={aiButtonProps.isActive}
          onClick={aiButtonProps.onClick}
        >
          {t(
            i18nLocale,
            'snowflakes:searchAndFilterBar.aiAssistant',
            'AI Assistant'
          )}
        </AiButton>
      )}
    </SearchAndFilterBarContainer>
  );
};

export { SearchAndFilterBar };
